import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { showToast } from "@/lib/toast";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

const ContactUs = () => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { error: contactError } = await supabase.functions.invoke('contact-us', {
        body: values
      });

      if (contactError) {
        console.error("Error submitting contact form:", contactError);
        showToast.error(t("contact.form.error"));
        return;
      }

      showToast.success(t("contact.form.success"));
      form.reset();
    } catch (error) {
      console.error("Error sending message:", error);
      showToast.error(t("contact.form.error"));
    }
  };

  return (
    // <div className="container mx-auto py-4">
        <div className="auth-pageitems-center justify-center py-4">

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl mx-auto"
      >
        <h1 className="font-archivoblack text-5xl font-bold mb-6 text-left text-[#34375B]">{t("contact.title")}</h1>
        <div className="bg-card p-6 rounded-lg shadow-sm border border-[#34375B]/20 border-1 ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 text-left">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("contact.form.name")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("contact.form.namePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("contact.form.email")}</FormLabel>
                    <FormControl>
                      <Input className="bg-white border border-gray-300 p-2 rounded-md" placeholder={t("seller_info.email_placeholder", "contact@gmail.com")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#8791F3]">{t("contact.form.message")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("contact.form.messagePlaceholder")}
                        className="min-h-[120px] bg-white border border-gray-300"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-center"> 
                <Button type="submit" className="w-1/2 bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200">
                  {t("contact.form.send")}
                </Button>
              </div>
              
              <div className="text-center mt-4 font-normal text-sm text-[#34375B]/70">
                {t("contact.form.alternative", "Or contact us at")} <a href="mailto:contact@billdrip.com" className="text-[#8791F3] hover:underline">contact@billdrip.com</a>
              </div>
            </form>
          </Form>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactUs;
